import { z } from 'zod'

import { packageOrigin } from '../content-bridge/package-origin'
import type { Cmi5LaunchParameters } from '../content-bridge/protocol'
import { packageUrl } from '../content-bridge/wrapper/wrapper'
import type { LaunchGrant } from '../launch-types'

/** The launch page for a launch's registration (begin or resume). */
export const cmi5LaunchRoute = '/launch/cmi5/:publicId'

export function cmi5LaunchPath(launchId: string): string {
  return `/launch/cmi5/${encodeURIComponent(launchId)}`
}

/** The API's launch page calls. */
const cmi5Api = '/launch/v1/cmi5'

/** The launch's unit, read without opening a session, so a unit running in another window keeps running. */
export function unitApi(launchId: string): string {
  return `${cmi5Api}/launches/${encodeURIComponent(launchId)}`
}

export function sessionApi(launchId: string): string {
  return `${unitApi(launchId)}/sessions`
}

export function runningApi(activityId: string, grant: LaunchGrant): string {
  return `${cmi5Api}/running?${new URLSearchParams({ activityId, ...grantFields(grant) }).toString()}`
}

function grantFields(grant: LaunchGrant): Readonly<Record<string, string>> {
  switch (grant.kind) {
    case 'enrollment':
      return { enrollmentId: grant.enrollmentId }
    case 'subscription':
      return { subscriptionId: grant.subscriptionId }
    case 'class':
      return { classId: grant.classId }
  }
}

const sessionSchema = z.object({
  launchId: z.string(),
  registrationId: z.string(),
  activityId: z.string(),
  name: z.string(),
  unitTitle: z.string(),
  launchMethod: z.enum(['anyWindow', 'ownWindow']),
  path: z.string().nullish(),
  url: z.string().nullish(),
  contentToken: z.string().nullish(),
  endpointPath: z.string(),
  fetchPath: z.string(),
  actor: z.string(),
  registration: z.string(),
  activityIri: z.string(),
  sessionId: z.string(),
})

/** One session of a cmi5 unit: what its launch URL carries, and where the unit is. */
export type Cmi5Session = z.infer<typeof sessionSchema>

export function parseSession(body: unknown): Cmi5Session {
  return sessionSchema.parse(body)
}

const unitSchema = z.object({
  launchId: z.string(),
  name: z.string(),
  unitTitle: z.string(),
  isFramed: z.boolean(),
  isSessionRunning: z.boolean(),
})

/** A launch's unit, as the page shows it before opening a session: whether it plays in the page's frame, and whether a session of it may be running elsewhere. */
export type Cmi5Unit = z.infer<typeof unitSchema>

export function parseUnit(body: unknown): Cmi5Unit {
  return unitSchema.parse(body)
}

/** The channel this browser's launch pages tell each other on which session is now a launch's: opening one ends the last. */
export const sessionChannelName = 'tms.cmi5-sessions'

// This page's own id: a BroadcastChannel skips only the object that sent, not the rest of its page.
const thisPage = crypto.randomUUID()

const sessionOpenedSchema = z.object({ launchId: z.string(), sessionId: z.string(), source: z.string().optional() })

/** A session a launch page opened. */
export interface SessionOpened {
  readonly launchId: string
  readonly sessionId: string
}

/** Tells this browser's other launch pages that this one opened a session of the launch. */
export function announceSession(opened: SessionOpened): void {
  if (typeof BroadcastChannel === 'undefined') {
    return
  }
  const channel = new BroadcastChannel(sessionChannelName)
  // oxlint-disable-next-line unicorn/require-post-message-target-origin -- a BroadcastChannel takes no target origin
  channel.postMessage({ launchId: opened.launchId, sessionId: opened.sessionId, source: thisPage })
  channel.close()
}

/** Calls `onOpened` when another of this browser's launch pages opens a session. Returns the unsubscribe. */
export function listenForSessions(onOpened: (opened: SessionOpened) => void): () => void {
  if (typeof BroadcastChannel === 'undefined') {
    return () => undefined
  }
  const channel = new BroadcastChannel(sessionChannelName)
  channel.addEventListener('message', (event: MessageEvent<unknown>) => {
    const parsed = sessionOpenedSchema.safeParse(event.data)
    if (parsed.success && parsed.data.source !== thisPage) {
      onOpened({ launchId: parsed.data.launchId, sessionId: parsed.data.sessionId })
    }
  })
  return () => channel.close()
}

/** The API's refusal to renew a framed unit's files once another page opened its launch again. */
export const sessionReplacedCode = 'launch.cmi5_session_replaced'

const runningSchema = z.object({ launchId: z.string(), registrationId: z.string() })

export function parseRunning(body: unknown): z.infer<typeof runningSchema> {
  return runningSchema.parse(body)
}

/** The launch URL's parameters (cmi5 section 8.1): the endpoint and fetch URL are on the content host. */
export function launchParameters(session: Cmi5Session, contentOrigin: string): Cmi5LaunchParameters {
  return {
    endpoint: `${contentOrigin}${session.endpointPath}`,
    fetch: `${contentOrigin}${session.fetchPath}`,
    actor: session.actor,
    registration: session.registration,
    activityId: session.activityIri,
  }
}

function isInPackage(session: Cmi5Session): session is Cmi5Session & { readonly path: string; readonly contentToken: string } {
  return typeof session.path === 'string' && typeof session.contentToken === 'string'
}

/**
 * The unit's launch URL for a window of its own: its https address, or its file on the content
 * host, on the package's own origin where each has one, with the launch parameters added to its
 * query. `null` when it is neither, or when there is no content host, where the record store and
 * the package's files are.
 */
export function unitUrl(session: Cmi5Session, contentOrigin: string, contentPackageOrigins = ''): string | null {
  if (contentOrigin === '') {
    return null
  }
  let url: URL | null = null
  if (isInPackage(session)) {
    const origin = packageOrigin(contentOrigin, contentPackageOrigins, session.contentToken)
    // As the wrapper resolves it, so a path never leaves the package.
    url = origin === '' ? null : packageUrl(origin, session.contentToken, session.path)
  } else if (typeof session.url === 'string' && URL.canParse(session.url) && new URL(session.url).protocol === 'https:') {
    url = new URL(session.url)
  }
  if (url === null) {
    return null
  }

  for (const [name, value] of Object.entries(launchParameters(session, contentOrigin))) {
    url.searchParams.set(name, value)
  }
  return url.href
}
